import type { PartnerClientWithSecret } from '@epilot/sdk/identity'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ClientForm } from './client-form'
import { ClientOrganizations } from './organizations'
import {
  ConfirmAction,
  ErrorNotice,
  Field,
  EPILOT_IDP_PATH,
  SecretDialog,
  StatusBadge
} from './shared'

import { identityClient } from '@/api/identity-client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useOidcClient, useRefreshIdentity } from '@/services/identity.service'

type Action = 'activate' | 'suspend' | 'retire' | 'rotate'
const actionInfo: Record<Action, { title: string; description: string }> = {
  activate: {
    title: 'Activate application',
    description:
      'Allow enabled organizations to sign in through this application.'
  },
  suspend: {
    title: 'Suspend application',
    description:
      'Block sign-in for all organizations and revoke provider tokens. You can activate it again. Existing partner sessions may continue.'
  },
  retire: {
    title: 'Retire application',
    description:
      'Permanently retire this client ID and revoke every organization’s access. This cannot be undone. Existing partner sessions may continue.'
  },
  rotate: {
    title: 'Rotate secret',
    description:
      'Generate a new secret, shown once after rotation. Choose how long the previous secret remains valid.'
  }
}

export const EpilotIdpApplicationDetail = () => {
  const { clientId = '' } = useParams()
  const query = useOidcClient(clientId)
  const refresh = useRefreshIdentity()
  const [action, setAction] = useState<Action>()
  const [secret, setSecret] = useState<PartnerClientWithSecret>()
  const [compromised, setCompromised] = useState(false)
  const [windowHours, setWindowHours] = useState('24')
  const client = query.data

  const confirm = async (reason: string) => {
    if (action === 'activate') await identityClient.activate(clientId)
    if (action === 'suspend') await identityClient.suspend(clientId, reason)
    if (action === 'retire') await identityClient.retire(clientId)
    if (action === 'rotate') {
      const hours = Number(windowHours)

      if (
        !compromised &&
        (!windowHours.trim() ||
          !Number.isInteger(hours) ||
          hours < 0 ||
          hours > 720)
      )
        throw new Error('Enter a whole number of hours from 0 to 720.')
      setSecret(
        await identityClient.rotateSecret(clientId, {
          compromise_mode: compromised,
          migration_window_seconds: compromised ? 0 : hours * 3600
        })
      )
    }
    void refresh()
  }

  return (
    <div className="space-y-4 text-primary">
      <Link className="text-sm underline" to={EPILOT_IDP_PATH}>
        Back to applications
      </Link>
      <ErrorNotice message={query.error?.message} />
      {query.isPending && <p role="status">Loading application…</p>}
      {client && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold">
                  {client.display_name}
                </h1>
                <StatusBadge status={client.status} />
              </div>
              <p className="text-sm text-muted-foreground">
                {client.client_id} · {client.environment}
              </p>
            </div>
            {/* Everyday actions only; retiring lives at the bottom of Settings, away from these. */}
            <div className="flex items-center gap-2">
              {(client.status === 'draft' || client.status === 'suspended') && (
                <Button onClick={() => setAction('activate')}>Activate</Button>
              )}
              {client.status === 'active' && (
                <Button onClick={() => setAction('suspend')} variant="outline">
                  Suspend
                </Button>
              )}
              {client.status !== 'retired' && (
                <Button
                  onClick={() => {
                    setCompromised(false)
                    setWindowHours('24')
                    setAction('rotate')
                  }}
                  variant="ghost"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  Rotate secret
                </Button>
              )}
            </div>
          </div>
          {client.status_reason && (
            <p className="text-sm text-muted-foreground">
              {client.status_reason}
            </p>
          )}
          <Tabs defaultValue="settings">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="organizations">Organizations</TabsTrigger>
            </TabsList>
            <TabsContent className="space-y-4" value="settings">
              <p className="text-sm text-muted-foreground">
                Authorization code · client_secret_basic · Configuration v
                {client.config_version}
              </p>
              <ClientForm client={client} key={client.config_version} />
              <p className="text-xs text-muted-foreground">
                Created {new Date(client.created_at).toLocaleString()} by{' '}
                {client.created_by || '—'} · Updated{' '}
                {new Date(client.updated_at).toLocaleString()} by{' '}
                {client.updated_by || '—'}
              </p>
              {client.status !== 'retired' && (
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-border p-4">
                  <div>
                    <p className="text-sm font-medium">
                      Retire this application
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Permanently blocks sign-in for every organization. The
                      client ID is never reused.
                    </p>
                  </div>
                  <Button
                    onClick={() => setAction('retire')}
                    variant="destructive"
                  >
                    Retire application
                  </Button>
                </div>
              )}
            </TabsContent>
            <TabsContent value="organizations">
              <ClientOrganizations client={client} />
            </TabsContent>
          </Tabs>
        </>
      )}
      {action && (
        <ConfirmAction
          askReason={action === 'suspend'}
          description={actionInfo[action].description}
          destructive={action === 'retire' || action === 'suspend'}
          onClose={() => setAction(undefined)}
          onConfirm={confirm}
          title={actionInfo[action].title}
        >
          {action === 'rotate' && (
            <div className="space-y-4">
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor="compromised-secret"
              >
                <Checkbox
                  checked={compromised}
                  id="compromised-secret"
                  onCheckedChange={(checked) =>
                    setCompromised(checked === true)
                  }
                />
                The previous secret is compromised; invalidate it immediately.
              </label>
              <Field
                id="rotation-hours"
                label="Previous secret validity (hours)"
              >
                <Input
                  disabled={compromised}
                  id="rotation-hours"
                  max={720}
                  min={0}
                  onChange={(event) => setWindowHours(event.target.value)}
                  step={1}
                  type="number"
                  value={windowHours}
                />
              </Field>
            </div>
          )}
        </ConfirmAction>
      )}
      {secret && (
        <SecretDialog client={secret} onClose={() => setSecret(undefined)} />
      )}
    </div>
  )
}
